/**
 * 견적서 작성/수정 모달.
 *
 * - 종류(일반/유지보수)/사업자/견적일자/담당(자동·readonly)/연락처/이메일/수신/프로젝트명
 * - 품목 표: 행 추가/제거, 품목·상세내용 자동완성 (주요 가격표), 금액 자동
 * - Remark textarea
 * - 합계 표시
 *
 * 유지보수 종류 (v2.104.0) — 표와 합계가 달라진다.
 *   일반    수량 · 공급단가 · 공급합계 · 비고(여러 줄 · v2.325.1) / 총계 2줄
 *   유지보수 수량 · 단가 · 월 청구금액 · 기간 · 합계 금액 / 총계 3줄
 *   + 계약기간(시작·종료) 칸이 생기고, 그 값이 Remark 맨 윗줄로 자동으로 나간다.
 *   + 「유지보수」를 고르면 회사별 기본 Remark 문구를 한 번 채워 준다(지우거나 고칠 수 있음).
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { suggestContacts } from '../../lib/partner-contact-api';
import { listMainProducts, type MainProductRow } from '../../lib/price-list-api';
import type { CostSheet } from '../../lib/cost-sheet-api';
import { createCostSheetFromDraft } from '../../lib/cost-sheet-from-quote';
import { COST_SHEETS_QK, ensureCostSheets, useCostSheets } from '../../components/cost-sheet/useCostSheets';
import { CostSheetWindow } from '../../components/cost-sheet/CostSheetWindow';
import {
  computeQuoteTotals,
  estimateQuoteLines,
  fetchContactDefaults,
  fetchContactOptions,
  fetchContactPersons,
  quoteDescColWidthPx,
  MAINTENANCE_REMARK_DEFAULT,
  QUOTE_VALIDITY_DEFAULT,
  QUOTE_VALIDITY_PRESETS,
  type QuoteCompany,
  type QuoteContactDefaults,
  type QuoteDetail,
  type QuoteDraft,
  type QuoteItemInput,
  type QuoteKind,
} from '../../lib/quote-api';
import { listPartnerCompanies, partnerNames } from '../../lib/lookup-api';
import { QuoteItemAutocomplete } from '../../components/QuoteItemAutocomplete';
import { SimpleAutocomplete } from '../../components/SimpleAutocomplete';
import { CalcNumInput } from '../../components/CalcNumInput';
import { PriceReferenceModal } from '../../components/quote/PriceReferenceModal';
import { DraggableModal } from '../../components/DraggableModal';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: QuoteDetail | null;
  /** create 모드 초안 미리채움 (엑셀 업로드 인식 결과 또는 기존 견적서 복사). */
  prefill?: QuoteDraft | null;
  /** 엑셀 인식 경고 — 폼 상단 배너로 표시 */
  prefillWarnings?: string[];
  /**
   * prefill 의 출처 (2026-07-30) — 상단 안내 배너 문구가 달라진다.
   * 'excel' = 엑셀 업로드 인식 / 'copy' = 기존 견적서 내용 복사 / 'cost' = 원가분석표에서(v2.234.0). 기본 'excel'.
   */
  prefillKind?: 'excel' | 'copy' | 'cost';
  /**
   * 영업 상세에서 연 경우 (2026-07-27) — 그 영업건의 사업자로 고정한다.
   * 견적서↔영업건은 사업자가 일치해야 연결되므로 선택을 막아 어긋남을 원천 차단.
   */
  lockedCompanyId?: QuoteCompany;
  /**
   * 종류 고정 (2026-08-28) — 영업 상세에서 연 경우 '일반' 으로 묶는다.
   * 유지보수 견적서는 영업진행에 연동하지 않기 때문(거래명세서·납품확인증이 기간을 모름).
   */
  lockedQuoteKind?: QuoteKind;
  /** 작성 모드 담당 기본값 — 영업건 담당자(없으면 종전대로 로그인 사용자) */
  defaultEmpId?: string | null;
  /** 제목 옆 부제 (예: 매출일련번호) */
  subtitle?: string;
  /**
   * 떠 있는 창 (v2.237.0 · 사용자 「원가분석 자료 보면서 동시에 견적서도 만들 수 있으면 — 창을 두 개, 원가창·견적창」).
   *   배경 막 없이 오른쪽에 뜨는 창(끌어 옮기고 모서리로 크기 조절) — 뒤의 원가분석표를 그대로 보고 고칠 수 있다.
   *   층(zIndex 40)은 다른 창보다 낮아, 표가 여는 가져오기·미리보기 창과 이 창의 💡 가격 참고 창(60)이 위로 온다.
   */
  floating?: boolean;
  /**
   * 「↻ 다시 가져오기」 — 뒤 화면(원가분석표)의 지금 값으로 품목·수량·단가를 다시 채운다 (v2.237.0).
   *   get() 이 null 이면 아무 일도 없다. 이 창에서 고친 품목·단가는 사라지므로 한 번 묻는다.
   */
  refreshPrefill?: { label: string; get: () => QuoteDraft | null };
  /**
   * 🧮 원가분석 창 (v2.248.0) — 새로 만드는 원가분석표를 이을 영업건(영업 상세에서 연 견적서). 없으면 수정 중인 견적서의 영업건.
   *   [🧮 원가분석] 단추는 원가분석표 쪽에서 연 창(refreshPrefill 있음)과 유지보수 견적서에는 없다.
   */
  costSalesId?: string | null;
  onClose: () => void;
  onSubmit: (data: {
    companyId: QuoteCompany;
    quoteKind: QuoteKind;
    quoteDate: string;
    contractStart: string;
    contractEnd: string;
    recipientCompany: string;
    reference: string;
    projectName: string;
    remark: string;
    validity: string;
    empName: string;
    empTitle: string;
    contactPhone: string;
    contactEmail: string;
    items: QuoteItemInput[];
    /** 🧮 이 창에서 연 원가분석표 — 서버가 저장하면서 그 표에 견적서 번호를 잇는다 (v2.248.0) */
    costSheetId?: number | null;
  }) => Promise<void>;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function emptyItem(): QuoteItemInput {
  return { itemName: '', description: '', qty: 1, unitPrice: 0, note: '', months: 1 };
}

export function QuoteFormModal({
  open,
  mode,
  initial,
  prefill,
  prefillWarnings,
  prefillKind = 'excel',
  lockedCompanyId,
  lockedQuoteKind,
  defaultEmpId,
  subtitle,
  floating = false,
  refreshPrefill,
  costSalesId,
  onClose,
  onSubmit,
}: Props) {
  const qc = useQueryClient();
  const [companyId, setCompanyId] = useState<QuoteCompany>('SM');
  const [quoteKind, setQuoteKind] = useState<QuoteKind>('일반');
  const [quoteDate, setQuoteDate] = useState<string>(todayIso());
  const [contractStart, setContractStart] = useState('');
  const [contractEnd, setContractEnd] = useState('');
  const [recipientCompany, setRecipientCompany] = useState('');
  const [reference, setReference] = useState('');
  const [projectName, setProjectName] = useState('');
  const [remark, setRemark] = useState('');
  const [validity, setValidity] = useState(QUOTE_VALIDITY_DEFAULT);
  const [items, setItems] = useState<QuoteItemInput[]>([emptyItem()]);
  const [contact, setContact] = useState<QuoteContactDefaults | null>(null);
  // 영업담당자 4명 리스트 (담당 select 옵션)
  const [contactOptions, setContactOptions] = useState<QuoteContactDefaults[]>([]);
  // 선택된 담당 (옵션의 empId 또는 빈 문자열 = "직접 입력")
  const [selectedEmpId, setSelectedEmpId] = useState<string>('');
  // 담당/연락처/이메일 — select 선택 시 자동 채움, 사용자 수동 수정 가능
  const [empDisplay, setEmpDisplay] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [products, setProducts] = useState<MainProductRow[]>([]);
  const [proposalCompanyOptions, setProposalCompanyOptions] = useState<string[]>([]);
  const [contactPersonOptions, setContactPersonOptions] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** [💡 가격 참고] 를 연 품목 줄 번호 (v2.235.0) — null 이면 닫힘 */
  const [refIdx, setRefIdx] = useState<number | null>(null);
  /**
   * 🧮 원가분석 창 (v2.248.0 · 사용자 「견적서 작성하다가도 원가분석」) — 이 창에서 연(또는 만든) 원가분석표 · 창이 떠 있나 · 위에 있는 창.
   *   ⚠이 창은 닫혀도 사라지지 않는다(아래 `if (!open) return null` 이 훅 뒤) → 열 때마다 아래 효과가 비운다.
   */
  const [costSheetId, setCostSheetId] = useState<number | null>(null);
  const [costOpen, setCostOpen] = useState(false);
  const [costBusy, setCostBusy] = useState(false);
  const [front, setFront] = useState<'quote' | 'cost'>('cost');
  const costStore = useCostSheets({ enabled: costOpen });
  useEffect(() => {
    if (!open) return;
    setCostSheetId(null);
    setCostOpen(false);
    setFront('cost');
  }, [open]);

  // 1) 모달 열릴 때 담당 정보 + 담당자 옵션 + 가격표 + 수신/참조 후보 로드
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, opts, p, pc, cp] = await Promise.all([
          fetchContactDefaults(),
          fetchContactOptions(),
          listMainProducts(),
          listPartnerCompanies(),
          fetchContactPersons(),
        ]);
        setContact(c);
        setContactOptions(opts);
        setProducts(p);
        setProposalCompanyOptions(partnerNames(pc, 'supplier'));
        setContactPersonOptions(cp);
      } catch (e) {
        setErr((e as Error).message);
      }
    })();
  }, [open]);

  // 참조 후보 = **수신(제안업체) 칸 회사의 거래처 담당자**만 (v2.293.0 · 사용자 10-23 「수신을 입력하면 참조 목록에 그 제안업체 담당자만」)
  //   거래처 담당자 주소록(영업진행 폼 담당자 칸과 같은 /partner-contacts/suggest · 같은 캐시 · 「이름 직급」).
  //   없으면 직접 입력 · 수신이 비어 있으면 전처럼 영업진행의 담당자 전체.
  //   운영 10-23: 견적 받은 회사 53곳 중 47곳이 주소록에 담당자가 있다 · 최근 견적 60건 중 48건의 참조가 그 회사 주소록에 있다.
  const recipientKey = recipientCompany.trim();
  const recipientContactsQ = useQuery({
    queryKey: ['partnerContactSuggest', recipientKey],
    queryFn: () => suggestContacts(recipientKey),
    enabled: open && recipientKey.length > 0,
    staleTime: 60_000,
  });
  const referenceCandidates = useMemo(
    () => (recipientKey ? (recipientContactsQ.data ?? []).map((c) => c.label) : contactPersonOptions),
    [recipientKey, recipientContactsQ.data, contactPersonOptions],
  );

  // 신규 작성 + contact 자동 채움 (사용자 수동 수정 전 1회)
  // 영업 상세에서 연 경우(defaultEmpId)는 그 영업건 담당자를 우선 — 남의 건을 대신
  // 작성해도 견적서에는 담당 영업사원이 찍히도록 (2026-07-27 사용자 확정).
  useEffect(() => {
    if (!open || mode !== 'create') return;
    const preferred =
      (defaultEmpId ? contactOptions.find((o) => o.empId === defaultEmpId) : null) ?? contact;
    if (!preferred) return;
    setEmpDisplay((cur) => cur || preferred.displayName);
    setContactPhone((cur) => cur || preferred.phone);
    setContactEmail((cur) => cur || preferred.email);
    setSelectedEmpId((cur) => cur || preferred.empId);
  }, [open, mode, contact, contactOptions, defaultEmpId]);

  // 수정 모드: initial 의 empId 가 옵션 중 하나면 그것 선택, 아니면 '직접 입력'
  useEffect(() => {
    if (!open || mode !== 'edit' || !initial || contactOptions.length === 0) return;
    const match = contactOptions.find((o) => o.empId === initial.empId);
    setSelectedEmpId(match ? match.empId : '');
  }, [open, mode, initial, contactOptions]);

  // 담당 select 변경 시 그 옵션의 displayName/phone/email 자동 채움
  function onSelectEmp(empId: string) {
    setSelectedEmpId(empId);
    if (!empId) return; // 빈 값 = '직접 입력' — 기존 값 유지
    const opt = contactOptions.find((o) => o.empId === empId);
    if (!opt) return;
    setEmpDisplay(opt.displayName);
    setContactPhone(opt.phone);
    setContactEmail(opt.email);
  }

  // 2) 초기값 (수정 모드)
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setCompanyId(initial.companyId);
      setQuoteKind(initial.quoteKind ?? '일반');
      setQuoteDate(initial.quoteDate);
      setContractStart(initial.contractStart ?? '');
      setContractEnd(initial.contractEnd ?? '');
      setRecipientCompany(initial.recipientCompany);
      setReference(initial.reference);
      setProjectName(initial.projectName);
      setRemark(initial.remark);
      setValidity(initial.validity || QUOTE_VALIDITY_DEFAULT);
      setEmpDisplay(initial.empDisplay ?? '');
      setContactPhone(initial.contactPhone ?? '');
      setContactEmail(initial.contactEmail ?? '');
      setItems(
        initial.items.map((it) => ({
          itemName: it.itemName,
          description: it.description,
          qty: it.qty,
          unitPrice: it.unitPrice,
          note: it.note,
          months: it.months ?? 1,
        })),
      );
    } else if (mode === 'create') {
      // prefill(엑셀 업로드 인식 결과 또는 기존 견적서 복사) 이 있으면 그 값으로, 없으면 빈 폼.
      // 사업자는 엑셀 서식으로는 판별 불가(prefill.companyId 없음) — 사용자가 확인/선택.
      // 기존 견적서 복사는 원본 사업자를 그대로 이어받는다 (2026-07-30).
      // 단 영업 상세에서 연 경우는 그 영업건 사업자로 고정(lockedCompanyId).
      setCompanyId(lockedCompanyId ?? prefill?.companyId ?? 'SM');
      setQuoteKind(lockedQuoteKind ?? prefill?.quoteKind ?? '일반');
      setQuoteDate(prefill?.quoteDate || todayIso());
      setContractStart(prefill?.contractStart ?? '');
      setContractEnd(prefill?.contractEnd ?? '');
      setRecipientCompany(prefill?.recipientCompany ?? '');
      setReference(prefill?.reference ?? '');
      setProjectName(prefill?.projectName ?? '');
      setRemark(prefill?.remark ?? '');
      setValidity(prefill?.validity || QUOTE_VALIDITY_DEFAULT);
      // 담당/연락처/이메일 — prefill 값 있으면 사용(후속 contact 자동채움은 빈 값일 때만 덮음)
      setEmpDisplay(prefill?.empName ?? '');
      setContactPhone(prefill?.contactPhone ?? '');
      setContactEmail(prefill?.contactEmail ?? '');
      // selectedEmpId reset — 이전 모달 세션 잔재 제거.
      // 후속 useEffect 에서 contact(본인 또는 황재상 폴백) 로 자동 설정됨.
      setSelectedEmpId('');
      setItems(prefill?.items && prefill.items.length > 0 ? prefill.items.map((it) => ({ ...it })) : [emptyItem()]);
    }
    setErr(null);
  }, [open, mode, initial, prefill, lockedCompanyId, lockedQuoteKind]);

  const isMaint = quoteKind === '유지보수';
  const totals = useMemo(() => computeQuoteTotals(items, quoteKind), [items, quoteKind]);
  /**
   * 원가 · 마진 (v2.235.0) — 원가분석표에서 넘어온 줄과 [💡 가격 참고]로 원가에서 계산한 줄은 costUnit 을 들고 있다.
   * 그 줄만 모아 원가 합계·마진을 보인다(원가를 모르는 줄은 빼고 「원가 아는 줄 N/M」 로 밝힌다). 일반 견적서만 — 유지보수는 월 단가라 기준이 다르다.
   */
  const costInfo = useMemo(() => {
    const known = items.filter((it) => it.costUnit != null && Number.isFinite(it.costUnit));
    if (known.length === 0) return null;
    const cost = known.reduce((a, it) => a + (Number(it.qty) || 0) * (it.costUnit as number), 0);
    const sell = known.reduce((a, it) => a + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0), 0);
    return { known: known.length, cost: Math.round(cost), sell: Math.round(sell), margin: Math.round(sell - cost), pct: sell > 0 ? ((sell - cost) / sell) * 100 : null };
  }, [items]);

  if (!open) return null;

  /** 🧮 원가분석 단추 — 일반 견적서이고, 원가분석표 쪽에서 연 창(↻ 다시 가져오기가 있는 창)이 아닐 때 */
  const costAvailable = !refreshPrefill && !isMaint;

  /**
   * [🧮 원가분석] — 이 창에서 이미 연 표 → 이 견적서에 이어진 표(수정) → 없으면 지금 품목으로 새 표를 만들어 왼쪽 창에 띄운다.
   *   새 표: 품목·규격·수량·비고 그대로 · 적어 둔 판매 단가는 고정(0 원 줄은 마진율 자동) · 원가는 💡 로 알던 값 → 품목 마스터 매입가.
   */
  async function openCostAnalysis() {
    if (costBusy) return;
    if (costOpen) { setFront('cost'); return; }
    setCostBusy(true);
    try {
      let id = costSheetId ?? (mode === 'edit' ? (initial?.costSheetId ?? null) : null);
      // 목록 캐시를 서버 목록으로 채워 둔다 — 새 표만 든 목록이 원가분석 탭에 잠깐 보이지 않게 · 이어진 표가 지워졌나도 여기서 본다
      let list = await ensureCostSheets(qc);
      if (id !== null && !list.some((s) => s.id === id)) {
        list = await ensureCostSheets(qc, true);
        if (!list.some((s) => s.id === id)) id = null;
      }
      if (id === null) {
        // 원가를 못 찾은 줄은 원가 0 — 창에서 [+ 매입 목록에서] 로 채운다
        const { sheet } = await createCostSheetFromDraft({
          title: projectName,
          customer: recipientCompany,
          items,
          quoteId: mode === 'edit' ? (initial?.id ?? null) : null,
          quoteNo: mode === 'edit' ? (initial?.quoteNo ?? null) : null,
          salesId: costSalesId ?? (mode === 'edit' ? (initial?.salesId ?? null) : null),
        });
        qc.setQueryData<CostSheet[]>(COST_SHEETS_QK, (old) => [sheet, ...(old ?? []).filter((s) => s.id !== sheet.id)]);
        // 영업 상세의 [🧮 원가분석 (N)] 숫자
        if (sheet.salesId) void qc.invalidateQueries({ queryKey: ['price-list', 'cost-sheets', 'by-sales'] });
        id = sheet.id;
      }
      setCostSheetId(id);
      setFront('cost');
      setCostOpen(true);
    } catch (e) {
      window.alert(`원가분석 창을 열지 못했습니다: ${(e as Error).message}`);
    } finally {
      setCostBusy(false);
    }
  }
  /** 원가분석 창의 [견적서에 반영 ➡] — 품목만 바꾼다(받는 곳·담당 같은 머리 칸은 그대로) */
  function applyFromCost(next: QuoteItemInput[], skipped: number) {
    const hasTyped = items.some((it) => it.itemName.trim() || it.description.trim() || Number(it.unitPrice));
    const msg = `견적서 품목을 원가분석표의 지금 값 ${next.length}줄(품목·규격·수량·판매 단가·비고)로 바꿉니다.` +
      (skipped > 0 ? `\n(품목명이 비었거나 수량이 0 인 줄 ${skipped}개는 넣지 않습니다)` : '') +
      (hasTyped ? '\n이 창에서 따로 고친 품목·단가는 사라집니다. 계속할까요?' : '');
    if (hasTyped && !window.confirm(msg)) return;
    setItems(next.map((it) => ({ ...it })));
    setFront('quote');
  }
  function closeCostWindow() {
    if (costSheetId !== null) void costStore.flush(costSheetId);
    setCostOpen(false);
  }

  /** [💡 가격 참고] 에서 고른 값 — 단가(+원가)는 넣고, 품목명·규격은 비어 있을 때만 채운다 */
  function applyPriceRef(idx: number, r: { unitPrice: number; costUnit: number | null; itemName?: string; description?: string }) {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        return {
          ...it,
          unitPrice: r.unitPrice,
          // 가격표·지난 견적의 단가(원가 없음)를 골라도 이미 알던 원가는 그대로 둔다 — 제안 단가가 바뀐 것이지 원가가 바뀐 게 아니다
          costUnit: r.costUnit ?? it.costUnit ?? null,
          itemName: it.itemName.trim() ? it.itemName : (r.itemName ?? it.itemName),
          description: it.description.trim() ? it.description : (r.description ?? it.description),
        };
      }),
    );
    setRefIdx(null);
  }

  /**
   * 종류 전환 — Remark 기본 문구를 넣고 빼는 규칙 (v2.104.0).
   * 넣기: 유지보수로 바꿨는데 Remark 가 비어 있으면 회사별 기본 문구를 채운다.
   * 빼기: 일반으로 되돌릴 때 **내가 넣은 그 문구 그대로일 때만** 지운다.
   *       사람이 고쳐 쓴 글은 건드리지 않는다.
   */
  function onChangeKind(next: QuoteKind) {
    const prev = quoteKind;
    if (next === prev) return;
    setQuoteKind(next);
    if (next === '유지보수') {
      setRemark((cur) => (cur.trim() ? cur : MAINTENANCE_REMARK_DEFAULT[companyId]));
    } else {
      setRemark((cur) =>
        cur.trim() === MAINTENANCE_REMARK_DEFAULT[companyId].trim() ? '' : cur,
      );
      setContractStart('');
      setContractEnd('');
    }
  }

  /** 사업자 전환 — 기본 문구를 넣어 둔 상태였다면 그 회사 문구로 바꿔 준다 (고쳐 쓴 글은 유지) */
  function onChangeCompany(next: QuoteCompany) {
    const prev = companyId;
    setCompanyId(next);
    if (!isMaint || prev === next) return;
    setRemark((cur) =>
      cur.trim() === MAINTENANCE_REMARK_DEFAULT[prev].trim()
        ? MAINTENANCE_REMARK_DEFAULT[next]
        : cur,
    );
  }

  function updateItem(idx: number, patch: Partial<QuoteItemInput>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function removeItem(idx: number) {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  }

  function addItem() {
    setItems((prev) => [...prev, emptyItem()]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (items.length === 0 || items.every((it) => !it.itemName && !it.description)) {
      setErr('품목을 1개 이상 입력해 주세요');
      return;
    }
    if (isMaint && contractStart && contractEnd && contractStart > contractEnd) {
      setErr('계약 종료일이 시작일보다 빠릅니다');
      return;
    }
    setSubmitting(true);
    try {
      // 🧮 원가분석 창에서 고친 것을 먼저 보낸다 — 견적서와 같이 저장된 표가 되게(서버가 견적서 번호를 잇는다)
      if (costSheetId !== null) await costStore.flush(costSheetId);
      await onSubmit({
        costSheetId: costSheetId ?? undefined,
        companyId,
        quoteKind,
        quoteDate,
        contractStart: isMaint ? contractStart : '',
        contractEnd: isMaint ? contractEnd : '',
        recipientCompany: recipientCompany.trim(),
        reference: reference.trim(),
        projectName: projectName.trim(),
        remark,
        validity: validity.trim() || QUOTE_VALIDITY_DEFAULT,
        empName: empDisplay.trim(),
        empTitle: '',
        contactPhone: contactPhone.trim(),
        contactEmail: contactEmail.trim(),
        items: items.map((it) => ({
          itemName: it.itemName.trim(),
          description: it.description,
          qty: Number(it.qty) || 0,
          unitPrice: Number(it.unitPrice) || 0,
          note: isMaint ? '' : it.note,
          months: isMaint ? Math.max(1, Math.round(Number(it.months) || 1)) : 1,
        })),
      });
      // 성공 시 부모가 onClose 호출
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // 폼 안 어디서든 Enter 키 입력 시 자동 submit 방지 — textarea·submit 버튼만 통과
  // 사용자 의도: 품목 자동완성 도중 엔터로 저장되지 않고 [저장] 버튼으로만 저장
  function blockEnterSubmit(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA') return;
    if (target.tagName === 'BUTTON' && (target as HTMLButtonElement).type === 'submit') return;
    e.preventDefault();
  }

  /** ↻ 다시 가져오기 — 뒤 화면(원가분석표)의 지금 값으로 품목만 다시 채운다(받는 곳·담당 같은 머리 칸은 그대로) */
  function refreshFromPrefill() {
    if (!refreshPrefill) return;
    const d = refreshPrefill.get();
    if (!d) {
      window.alert('가져올 원가분석표를 찾지 못했습니다.');
      return;
    }
    if (!window.confirm('품목·수량·단가를 원가분석표의 지금 값으로 바꿉니다.\n이 창에서 고친 품목·단가는 사라집니다. 계속할까요?')) return;
    setItems(d.items.length > 0 ? d.items.map((it) => ({ ...it })) : [emptyItem()]);
  }

  /** 창 모양 — 원가분석 탭이 띄운 떠 있는 창이거나, 🧮 원가분석 창과 나란히 뜬 창(costPair) */
  const costPair = !floating && costOpen && costSheetId !== null;
  const windowed = floating || costPair;
  const formEl = (
      <form
        onSubmit={handleSubmit}
        onKeyDown={blockEnterSubmit}
        className={windowed ? 'flex flex-col flex-1 min-h-0' : 'bg-white rounded shadow-xl w-full max-w-5xl my-4'}
        onClick={windowed ? undefined : (e) => e.stopPropagation()}
        onPointerDownCapture={costPair ? () => setFront('quote') : undefined}
      >
        <div className={`px-5 py-3 border-b border-slate-200 flex items-center justify-between ${windowed ? 'dm-drag-handle cursor-move select-none bg-slate-50' : ''}`}>
          <h2 className="ui-modal-title">
            {mode === 'create' ? '견적서 작성' : '견적서 수정'}
            {subtitle && (
              <span className="ml-2 text-sm font-normal text-slate-500">{subtitle}</span>
            )}
            {floating && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                — 떠 있는 창 · 머리를 끌어 옮기고 오른쪽 아래 모서리로 크기 조절 · 뒤의 원가분석표를 그대로 보고 고칠 수 있습니다
              </span>
            )}
            {costPair && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                — 왼쪽 🧮 원가분석 창에서 계산하고 [견적서에 반영 ➡] · 누른 창이 위로 옵니다
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className={`p-5 space-y-4 ${windowed ? 'flex-1 min-h-0 overflow-y-auto' : ''}`}>
          {/* 미리채움 안내 (create + prefill 시) — 엑셀 인식 / 기존 견적서 복사 */}
          {mode === 'create' && prefill && (
            <div
              className={
                prefillKind === 'copy'
                  ? 'px-3 py-2 bg-sky-50 border border-sky-300 rounded text-xs text-sky-900 space-y-1'
                  : prefillKind === 'cost'
                    ? 'px-3 py-2 bg-emerald-50 border border-emerald-300 rounded text-xs text-emerald-900 space-y-1'
                    : 'px-3 py-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900 space-y-1'
              }
            >
              <div className="font-bold">
                {prefillKind === 'copy'
                  ? '📋 기존 견적서 내용을 복사해 왔습니다. 원본은 그대로 남고, 새 견적번호로 저장됩니다. 확인·수정 후 [확인]을 누르세요.'
                  : prefillKind === 'cost'
                    ? '🧮 원가분석표의 품목을 가져왔습니다. 단가는 마진율로 계산한 판매 단가입니다 — 받는 곳·담당·단가를 확인·수정한 뒤 [확인]으로 저장하세요.'
                    : '📥 엑셀에서 인식한 내용을 미리 채웠습니다. 확인·수정 후 [확인]으로 저장하세요.'}
              </div>
              {prefillWarnings && prefillWarnings.length > 0 && (
                <ul className="list-disc pl-5 space-y-0.5">
                  {prefillWarnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* 헤더 정보 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Field label="종류" wide>
              <div className="flex flex-wrap items-center gap-4">
                {(['일반', '유지보수'] as const).map((k) => (
                  <label
                    key={k}
                    className={
                      lockedQuoteKind ? 'flex items-center gap-1.5' : 'flex items-center gap-1.5 cursor-pointer'
                    }
                    title={
                      lockedQuoteKind
                        ? '영업 상세에서 만드는 견적서는 「일반」 으로 고정됩니다 (유지보수 건은 유지보수 계약에서 관리)'
                        : undefined
                    }
                  >
                    <input
                      type="radio"
                      name="quoteKind"
                      value={k}
                      checked={quoteKind === k}
                      disabled={!!lockedQuoteKind}
                      onChange={() => onChangeKind(k)}
                    />
                    <span
                      className={
                        quoteKind === k
                          ? lockedQuoteKind
                            ? 'font-bold text-slate-500'
                            : 'font-bold text-slate-800'
                          : 'text-slate-400'
                      }
                    >
                      {k === '일반' ? '일반 견적서' : '유지보수 견적서'}
                    </span>
                  </label>
                ))}
                <span className="text-xs text-slate-500">
                  {isMaint
                    ? '품목마다 기간(개월)을 넣고, 월 청구금액 · 계약기간 합계로 계산됩니다'
                    : '수량 × 공급단가로 계산됩니다'}
                </span>
              </div>
            </Field>
            <Field label="사업자">
              <select
                value={companyId}
                onChange={(e) => onChangeCompany(e.target.value as QuoteCompany)}
                disabled={!!lockedCompanyId}
                title={
                  lockedCompanyId
                    ? '영업건의 사업자로 고정됩니다 (견적서와 영업건의 사업자는 같아야 연결됩니다)'
                    : undefined
                }
                className="ui-input w-full"
              >
                <option value="SM">SM — (주)에스엠시스템즈</option>
                <option value="EJ">EJ — (주)이진아이씨티</option>
              </select>
            </Field>
            <Field label="견적일자">
              <input
                type="date"
                value={quoteDate}
                onChange={(e) => setQuoteDate(e.target.value)}
                className="ui-input w-full"
              />
            </Field>
            {isMaint && (
              <Field label="계약기간" wide>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={contractStart}
                    onChange={(e) => setContractStart(e.target.value)}
                    className="ui-input"
                  />
                  <span className="text-slate-400">~</span>
                  <input
                    type="date"
                    value={contractEnd}
                    onChange={(e) => setContractEnd(e.target.value)}
                    className="ui-input"
                  />
                  <span className="text-xs text-slate-500">
                    Remark 맨 윗줄에 「* 계약기간 : …」 로 자동으로 들어갑니다
                  </span>
                </div>
              </Field>
            )}
            {/* 📅 견적 유효 (v2.326.0) — 처음엔 「발행 후 1주일」 · 직접 적거나 단추로 고른다 · PDF·미리보기·엑셀에 적은 그대로 */}
            <Field label="견적 유효">
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={validity}
                  onChange={(e) => setValidity(e.target.value)}
                  maxLength={100}
                  placeholder={QUOTE_VALIDITY_DEFAULT}
                  title="견적서의 「견 적 유 효 :」 칸에 적은 그대로 찍힙니다 — 예: 발행 후 2주일 · 2026년 12월 31일까지"
                  className="ui-input flex-1 min-w-0"
                />
                {QUOTE_VALIDITY_PRESETS.map((p) => {
                  const v = `발행 후 ${p}`;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setValidity(v)}
                      className={`btn btn-sm shrink-0 ${validity.trim() === v ? 'btn-primary' : 'btn-outline'}`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="담당">
              <div className="space-y-1">
                <select
                  value={selectedEmpId}
                  onChange={(e) => onSelectEmp(e.target.value)}
                  className="ui-input w-full"
                >
                  <option value="">— 영업담당자 선택 (또는 직접 입력) —</option>
                  {contactOptions.map((opt) => (
                    <option key={opt.empId} value={opt.empId}>
                      {opt.displayName}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={empDisplay}
                  onChange={(e) => setEmpDisplay(e.target.value)}
                  placeholder="담당자 이름·직책 (자동 채움됨)"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs text-slate-600"
                />
              </div>
            </Field>
            <Field label="연락처">
              <input
                type="text"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder={contact?.phone ?? '010-xxxx-xxxx'}
                className="ui-input w-full"
              />
            </Field>
            <Field label="이메일">
              <input
                type="text"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder={contact?.email ?? 'email@smsys.co.kr'}
                className="ui-input w-full"
              />
            </Field>
            <Field label="수신 (제안업체)">
              <SimpleAutocomplete
                value={recipientCompany}
                onChange={setRecipientCompany}
                candidates={proposalCompanyOptions}
                placeholder="예: 서경산업 (영업진행에서 검색 또는 직접 입력)"
              />
            </Field>
            <Field label="참조 (거래처 담당자)">
              <SimpleAutocomplete
                value={reference}
                onChange={setReference}
                candidates={referenceCandidates}
                {...(recipientKey ? {
                  heading: `「${recipientKey}」 거래처 담당자`,
                  emptyText: recipientContactsQ.isLoading
                    ? '담당자를 불러오는 중…'
                    : `주소록에 「${recipientKey}」 담당자가 없습니다 — 위 [직접 입력] 으로`,
                  // 한 회사 담당자는 다 보여 준다(운영 최다 31명 · 목록은 스크롤) — 치면 좁혀진다
                  max: 100,
                } : {})}
                placeholder={recipientKey
                  ? `예: 강동훈 이사님 (「${recipientKey}」 담당자 중에서 고르거나 직접 입력)`
                  : '예: 강동훈 이사님 (영업진행에서 검색 또는 직접 입력)'}
              />
            </Field>
            <Field label="프로젝트명" wide>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="예: 강남구 고도화 사업"
                className="ui-input w-full"
              />
            </Field>
          </div>

          {/* 품목 표 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <h3 className="ui-subsection-title">
                품목 ({items.length}건)
                <span className="ml-2 text-xs font-normal text-slate-500">
                  💡 수량·단가에 「=150*2」 같은 수식 입력 가능 (엑셀식 · Enter/다른 칸 클릭 시 계산)
                </span>
              </h3>
              <div className="flex items-center gap-1.5">
                {/* 🧮 원가분석 (v2.248.0) — 지금 품목으로 원가분석표를 만들어(또는 이어진 표를 열어) 왼쪽 창에 띄운다 */}
                {costAvailable && (
                  <button
                    type="button"
                    onClick={() => { void openCostAnalysis(); }}
                    disabled={costBusy}
                    title={costOpen
                      ? '왼쪽 원가분석 창을 앞으로'
                      : '지금 품목으로 원가분석표를 만들어 옆 창에 띄웁니다 — 매입가·인포트렌드 원가 · 마진율 · 목표 금액으로 단가를 정하고 [견적서에 반영]'}
                    className="btn btn-success-solid btn-sm"
                  >
                    {costBusy ? '여는 중…' : costOpen ? '🧮 원가분석 창 ←' : '🧮 원가분석'}
                  </button>
                )}
                {refreshPrefill && (
                  <button
                    type="button"
                    onClick={refreshFromPrefill}
                    title="뒤의 원가분석표에서 고친 것(줄·수량·마진율·판매 단가)을 이 창의 품목에 다시 반영합니다 — 이 창에서 고친 품목·단가는 사라집니다"
                    className="btn btn-success btn-sm"
                  >
                    ↻ {refreshPrefill.label}
                  </button>
                )}
                <button
                  type="button"
                  onClick={addItem}
                  className="btn btn-outline btn-sm"
                >
                  + 품목 추가
                </button>
              </div>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-1 py-1.5 w-8 text-center text-slate-600 font-medium">#</th>
                    <th className="px-1 py-1.5 w-44 min-w-[7rem] text-left text-slate-600 font-medium">품목</th>
                    {/* 최소 폭 — 떠 있는 좁은 창(700px 대)에서 이 칸이 0 에 가깝게 눌려 글자가 한 자씩 꺾였다(10-09 1366 실측) → 표가 옆으로 스크롤되게
                        · 🧮 원가분석 창과 나란히일 땐 11.5rem — 그 대신 품목·수량·단가 칸도 하한을 두어 단가 숫자가 잘리지 않게(10-13 1536 실측 「353,00」) */}
                    <th className={`px-1 py-1.5 text-left text-slate-600 font-medium ${costPair ? 'min-w-[11.5rem]' : 'min-w-[14rem]'}`}>상세 내용</th>
                    <th className="px-1 py-1.5 w-16 min-w-[3.5rem] text-right text-slate-600 font-medium">수량</th>
                    {isMaint ? (
                      <>
                        <th className="px-1 py-1.5 w-24 min-w-[6.5rem] text-right text-slate-600 font-medium">단가</th>
                        <th className="px-1 py-1.5 w-24 text-right text-slate-600 font-medium">월 청구금액</th>
                        <th className="px-1 py-1.5 w-20 text-right text-slate-600 font-medium">기간(개월)</th>
                        <th className="px-1 py-1.5 w-24 text-right text-slate-600 font-medium">합계 금액</th>
                      </>
                    ) : (
                      <>
                        <th className="px-1 py-1.5 w-28 min-w-[6.5rem] text-right text-slate-600 font-medium">공급단가</th>
                        <th className="px-1 py-1.5 w-28 text-right text-slate-600 font-medium">공급합계</th>
                        <th className="px-1 py-1.5 w-28 min-w-[4rem] text-left text-slate-600 font-medium">비고</th>
                      </>
                    )}
                    <th className="px-1 py-1.5 w-8 text-center text-slate-600 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-t border-slate-100 align-top">
                      <td className="px-1 py-1 text-center text-slate-500">{idx + 1}</td>
                      <td className="px-1 py-1">
                        <QuoteItemAutocomplete
                          field="name"
                          value={it.itemName}
                          products={products}
                          onChange={(v) => updateItem(idx, { itemName: v })}
                          onPick={(p) =>
                            updateItem(idx, {
                              itemName: p.name,
                              description: p.description || it.description,
                              unitPrice: Number(p.supplyPrice) || it.unitPrice,
                            })
                          }
                        />
                        {/* 💡 가격 참고 (v2.235.0) — 주요 가격표 · 매입가 · 인포트렌드 · 지난 견적을 보고 단가를 정한다 */}
                        <button
                          type="button"
                          onClick={() => setRefIdx(idx)}
                          className="mt-0.5 text-[11px] text-amber-700 hover:text-amber-900 hover:underline"
                          title="주요 가격표 · 매입가 · 인포트렌드 가격표 · 지난 견적 단가를 보고 이 줄의 단가를 정합니다"
                        >
                          💡 가격 참고
                        </button>
                      </td>
                      <td className="px-1 py-1">
                        <QuoteItemAutocomplete
                          field="description"
                          value={it.description}
                          products={products}
                          onChange={(v) => updateItem(idx, { description: v })}
                          onPick={(p) =>
                            updateItem(idx, {
                              description: p.description,
                              itemName: p.name || it.itemName,
                              unitPrice: Number(p.supplyPrice) || it.unitPrice,
                            })
                          }
                          inputMaxWidthPx={quoteDescColWidthPx(companyId, quoteKind)}
                        />
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                          <span
                            className={
                              estimateQuoteLines(it.description, companyId, quoteKind) >= 8
                                ? 'text-amber-700'
                                : ''
                            }
                          >
                            엑셀에서 {estimateQuoteLines(it.description, companyId, quoteKind)}줄
                          </span>
                          <span className="text-slate-300">·</span>
                          <span title="입력창 폭 = Excel 상세내용 열 너비. 가로 끝에서 줄바꿈됨">
                            {companyId} 기준 가로 폭 동기화
                          </span>
                        </div>
                      </td>
                      <td className="px-1 py-1">
                        <CalcNumInput
                          value={it.qty}
                          onCommit={(n) => updateItem(idx, { qty: n })}
                          ariaLabel="수량"
                          className="w-full px-1.5 py-1 text-xs text-right border border-slate-300 rounded"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <CalcNumInput
                          value={it.unitPrice}
                          onCommit={(n) => updateItem(idx, { unitPrice: n })}
                          ariaLabel={isMaint ? '단가' : '공급단가'}
                          className="w-full px-1.5 py-1 text-xs text-right border border-slate-300 rounded"
                        />
                        {it.costUnit != null && (() => {
                          // 원가 · 줄 마진율(판매가 기준) — 단가를 고치면 바로 바뀐다 · 음수면 빨강
                          const up = Number(it.unitPrice) || 0;
                          const mp = up > 0 ? ((up - it.costUnit) / up) * 100 : null;
                          return (
                            <div
                              className={`text-[11px] mt-0.5 text-right whitespace-nowrap ${mp !== null && mp < 0 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}
                              title="원가 단가 · 이 줄의 마진율(판매가 기준) — 원가분석표 또는 💡 가격 참고에서 따라온 원가"
                            >
                              원가 {it.costUnit.toLocaleString()} · 마진 {mp === null ? '—' : `${mp.toFixed(1)}%`}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-1 py-1 text-right text-slate-700 font-medium">
                        {totals.lineTotals[idx]?.toLocaleString() ?? 0}
                      </td>
                      {isMaint ? (
                        <>
                          <td className="px-1 py-1">
                            <CalcNumInput
                              value={it.months}
                              onCommit={(n) =>
                                updateItem(idx, { months: Math.max(1, Math.round(n) || 1) })
                              }
                              ariaLabel="기간(개월)"
                              className="w-full px-1.5 py-1 text-xs text-right border border-slate-300 rounded"
                            />
                          </td>
                          <td className="px-1 py-1 text-right text-slate-800 font-bold">
                            {totals.periodTotals[idx]?.toLocaleString() ?? 0}
                          </td>
                        </>
                      ) : (
                        <td className="px-1 py-1">
                          <NoteTextarea
                            value={it.note}
                            onChange={(v) => updateItem(idx, { note: v })}
                          />
                        </td>
                      )}
                      <td className="px-1 py-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          disabled={items.length === 1}
                          className="text-rose-600 hover:text-rose-800 disabled:text-slate-300 text-base leading-none"
                          title="행 삭제"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  {isMaint ? (
                    <>
                      <tr>
                        <td colSpan={7} className="px-2 py-1.5 text-right text-xs text-slate-600 font-medium">
                          월 청구금액 총계 (VAT 별도)
                        </td>
                        <td className="px-2 py-1.5 text-right text-sm font-bold text-slate-800">
                          {totals.totalMonthly.toLocaleString()}
                        </td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={7} className="px-2 py-1.5 text-right text-xs text-slate-600 font-medium">
                          계약기간 청구금액 총계 (VAT 별도)
                        </td>
                        <td className="px-2 py-1.5 text-right text-sm font-bold text-slate-800">
                          {totals.totalSupply.toLocaleString()}
                        </td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={7} className="px-2 py-1.5 text-right text-xs text-slate-600 font-medium">
                          계약기간 청구금액 총계 (VAT 포함)
                        </td>
                        <td className="px-2 py-1.5 text-right text-sm font-bold text-emerald-700">
                          {totals.totalWithVat.toLocaleString()}
                        </td>
                        <td></td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr>
                        <td colSpan={5} className="px-2 py-1.5 text-right text-xs text-slate-600 font-medium">
                          공급가 총계 (VAT 별도)
                        </td>
                        <td className="px-2 py-1.5 text-right text-sm font-bold text-slate-800">
                          {totals.totalSupply.toLocaleString()}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                      <tr>
                        <td colSpan={5} className="px-2 py-1.5 text-right text-xs text-slate-600 font-medium">
                          공급가 총계 (VAT 포함)
                        </td>
                        <td className="px-2 py-1.5 text-right text-sm font-bold text-emerald-700">
                          {totals.totalWithVat.toLocaleString()}
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                      {costInfo && (
                        <tr className="bg-amber-50/60">
                          <td colSpan={5} className="px-2 py-1.5 text-right text-xs text-amber-900 font-medium">
                            원가 합계 {costInfo.cost.toLocaleString()} · 마진{' '}
                            <span className={costInfo.margin < 0 ? 'text-red-600 font-bold' : ''}>{costInfo.margin.toLocaleString()}</span>
                            {costInfo.pct !== null && (
                              <span className={costInfo.pct < 0 ? 'text-red-600 font-bold' : ''}> ({costInfo.pct.toFixed(1)}%)</span>
                            )}
                            <span className="text-amber-700/80 font-normal">
                              {' '}— 원가 아는 줄 {costInfo.known}/{items.length} 기준 ·{' '}
                              {costSheetId !== null ? '원가는 🧮 원가분석표에 저장됩니다' : '저장되지 않는 참고 숫자'}
                            </span>
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      )}
                    </>
                  )}
                </tfoot>
              </table>
            </div>
          </div>

          {/* Remark */}
          <Field label="Remark" wide>
            <div className="space-y-1">
              {isMaint && (contractStart || contractEnd) && (
                <div className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-xs text-slate-500">
                  맨 윗줄에 자동으로 붙습니다 —{' '}
                  <span className="text-slate-700">
                    * 계약기간 : {(contractStart || contractEnd).replace(/-/g, '.')}
                    {contractStart && contractEnd ? ` ~ ${contractEnd.replace(/-/g, '.')}` : ''}
                  </span>
                </div>
              )}
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={3}
                placeholder="비고/특이사항 (선택)"
                className="ui-input ui-textarea w-full resize-y"
              />
            </div>
          </Field>

          {err && (
            <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-xs">
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? '저장 중...' : mode === 'create' ? '확인' : '수정'}
          </button>
        </div>

        {/* 💡 가격 참고 창 (v2.235.0) — <form> 안에 그려지므로 단추는 전부 type="button" · Enter 는 blockEnterSubmit 이 막는다 */}
        {refIdx !== null && items[refIdx] && (
          <PriceReferenceModal
            itemName={items[refIdx].itemName}
            description={items[refIdx].description}
            currentUnitPrice={Number(items[refIdx].unitPrice) || 0}
            products={products}
            onApply={(r) => applyPriceRef(refIdx, r)}
            onClose={() => setRefIdx(null)}
          />
        )}
      </form>
  );

  // 떠 있는 창 — 오른쪽에 화면 폭의 절반쯤(700~980px) · 배경 막 없음 · 층 40(표가 여는 창·💡 가격 참고 창이 위로 온다)
  if (floating) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // 화면 폭의 6할(1366 → 820 · 1536 → 922 · 1920 이상 → 980) — 좁은 화면은 표가 더 가려지지만 창을 끌어 옮기면 된다
    const w = Math.max(700, Math.min(980, Math.round(vw * 0.6)));
    return (
      <DraggableModal
        onClose={onClose}
        backdrop={false}
        zIndex={40}
        initialWidth={w}
        initialHeight={vh - 32}
        initialX={vw - w - 16}
        initialY={16}
        minWidth={640}
        bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col"
      >
        {formEl}
      </DraggableModal>
    );
  }
  // 🧮 원가분석 창과 나란히 (v2.248.0) — 배경 막은 그대로(뒤 목록을 못 누르게) 두고 그 위에 왼쪽 원가분석 창 · 오른쪽 견적서 창.
  //   폭(10-13 실측): 견적서 = 화면의 45.5%(686~920 · 품목 표 하한 ≈642px) · 원가분석 = 남은 폭(접힌 표 하한 781px → 창 812 이상).
  //     1920(874+1010) · 1536(699+812) 은 안 겹친다 · 1366·1280 은 원가분석 창이 812 를 지키며 조금 겹친다 — 누른 창이 위로 온다.
  //   층: 막 50 < 아래 창 51 < 위 창 52 — 각 창이 여는 창(💡 가격 참고 · 가져오기)은 그 창 층 안에서 뜬다.
  if (costPair) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const quoteW = Math.max(686, Math.min(920, Math.round(vw * 0.455)));
    const costW = Math.max(Math.min(812, vw - 400), Math.min(1200, vw - quoteW - 36));
    return (
      <>
        <div className="fixed inset-0 z-50 bg-black/40" />
        <DraggableModal
          onClose={onClose}
          backdrop={false}
          zIndex={front === 'quote' ? 52 : 51}
          initialWidth={quoteW}
          initialHeight={vh - 24}
          initialX={vw - quoteW - 12}
          initialY={12}
          minWidth={560}
          bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col"
        >
          {formEl}
        </DraggableModal>
        <CostSheetWindow
          store={costStore}
          sheetId={costSheetId}
          canEdit
          zIndex={front === 'cost' ? 52 : 51}
          initialX={12}
          initialWidth={costW}
          onFocus={() => setFront('cost')}
          onApply={applyFromCost}
          onClose={closeCostWindow}
        />
      </>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      {formEl}
    </div>
  );
}

// CalcNumInput — components/CalcNumInput 으로 공용 추출 (2026-06-10, 매입 폼 공유)

/** 품목 비고 — Enter 로 줄을 바꾸는 여러 줄 칸 · 높이는 내용에 맞춘다 (v2.325.1)
 *  <form> 의 blockEnterSubmit 이 textarea 는 통과시키므로 Enter 가 저장이 아니라 줄바꿈이 된다. */
function NoteTextarea({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`; // + 위아래 테두리(border-box)
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={1}
      maxLength={500}
      placeholder="비고"
      className="ui-input ui-input-sm ui-textarea w-full min-h-[1.75rem] py-1 leading-snug resize-none overflow-hidden"
    />
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className="ui-label block mb-0.5">{label}</label>
      {children}
    </div>
  );
}
